import { create } from 'zustand';
import { errorHandler } from '@/utils/functions/errorHandler';
import { IOrgKPI } from '@/types/kpi.types';
import { getKpis, getKpiDetail, createKpi, updateKpi, deleteKpi, KpiCreatePayload } from '@/api/kpi';

interface PaginationParams {
  page?: number;
  page_size?: number;
  order?: 'asc' | 'desc';
  order_by?: string;
  org_chart_node_id?: number;
  search?: string;
}

interface PaginatedData {
  content: IOrgKPI[];
  totalElements: number;
  totalPages: number;
}

interface KpiState {
  kpis: IOrgKPI[];
  paginatedData: PaginatedData | null;
  isLoading: boolean;
  error: unknown;

  fetchKpis: (tenantId: string | number, params?: PaginationParams) => Promise<void>;
  fetchKpiDetail: (tenantId: string | number, id: string | number) => Promise<IOrgKPI | void>;
  createKpi: (tenantId: string | number, payload: KpiCreatePayload) => Promise<IOrgKPI | void>;
  updateKpi: (tenantId: string | number, id: string | number, payload: KpiCreatePayload) => Promise<void>;
  deleteKpi: (tenantId: string | number, id: string | number) => Promise<void>;
}

const useKpiState = create<KpiState>(set => ({
  kpis: [],
  paginatedData: null,
  isLoading: false,
  error: null,

  fetchKpis: async (tenantId, params) => {
    set({ isLoading: true, error: null });
    try {
      const data = await getKpis(tenantId, params as Record<string, unknown>);
      const content: IOrgKPI[] = data?.data?.content ?? data?.data ?? data?.content ?? [];
      const total: number = data?.data?.totalElements ?? data?.totalElements ?? content.length;
      const limit = data?.data?.size ?? params?.page_size ?? 10;
      const totalPages: number = data?.data?.totalPages ?? data?.totalPages ?? (Math.ceil(total / limit) || 1);

      set({ kpis: content, paginatedData: { content, totalElements: total, totalPages } });
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  fetchKpiDetail: async (tenantId, id) => {
    set({ isLoading: true, error: null });
    try {
      const data = await getKpiDetail(tenantId, id);
      return data?.data ?? data;
    } catch (error) {
      set({ error });
      errorHandler(error);
    } finally {
      set({ isLoading: false });
    }
  },

  createKpi: async (tenantId, payload) => {
    try {
      const data = await createKpi(tenantId, payload);
      return data?.data ?? data;
    } catch (error) {
      errorHandler(error);
      throw error;
    }
  },

  updateKpi: async (tenantId, id, payload) => {
    try {
      await updateKpi(tenantId, id, payload);
    } catch (error) {
      errorHandler(error);
      throw error;
    }
  },

  deleteKpi: async (tenantId, id) => {
    try {
      await deleteKpi(tenantId, id);
    } catch (error) {
      errorHandler(error);
      throw error;
    }
  },
}));

export default useKpiState;
